import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import API from "@/lib/api";

/**
 * AgentNotesPanel — composer-only. The solicitor posts an ad-hoc update
 * that appears on the read-only Agent Link page. The case file itself
 * doesn't render the feed (user preference: "solicitor doesn't need to
 * see agent notes on the case file — only on the agent page"). Manual
 * notes still auto-append alongside milestone auto-notes on the portal.
 */
export default function AgentNotesPanel({ caseId, onCaseUpdated }) {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/agent-notes`, { text: text.trim() });
      onCaseUpdated?.(data.case);
      setText("");
      toast.success("Update posted to agent link");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post update");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="agent-notes-panel">
      <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <ChatCircle weight="bold" className="w-4 h-4 text-slate-500" /> Post update to agent
      </h4>
      <div className="space-y-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              post();
            }
          }}
          placeholder="Write a note — only the agent will see this on the Agent Link page…"
          className="text-xs min-h-[68px] resize-y"
          data-testid="agent-notes-composer-input"
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">Cmd/Ctrl + Enter to send</p>
          <Button
            onClick={post}
            disabled={posting || !text.trim()}
            className="h-7 px-3 text-xs bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="agent-notes-post-btn"
          >
            <PaperPlaneTilt weight="bold" className="w-3 h-3 mr-1" />
            {posting ? "Posting…" : "Post update"}
          </Button>
        </div>
      </div>
    </div>
  );
}
